import Penpal, { IChildConnectionObject, IChildConnectionOptions } from 'penpal';
import * as tracker from '../../common/tools/tracker';

import {
  CONFIG_PENPAL_CONNECTION_TIMEOUT,
} from '../../config';

import {
  _PermissionStatus,
  PermissionModel,
  LayerParameters,
  PermissionData,
  AditionNextData,
} from '../../interfaces';

import * as Logger from '../../common/tools/logger';
import { AditionIframe } from './adition-iframe.interface';
import { PermissionLayer, PermissionLayerOptions, TcData } from '../../layer/interfaces';
import { CommunicationHandlerService } from './communication-handler.service';
import { Dimensions } from '../../client/interfaces';
import { buildAditionRequest, getCacheBuster } from './adition.util';

/**
 * Class for all about ADITION
 */
export class AditionService {

  private moduleName = `AditionService`;
  private iframe: HTMLIFrameElement;
  private windowId: string = getCacheBuster(); // Needs to be the same through the lifetime of a PPP instance!
  private communicationHandler: CommunicationHandlerService;
  private childConnection: IChildConnectionObject<IChildConnectionOptions>;
  private layerCount = 0;
  private initTime: number;
  private layerViewTime: number;

  /**
   * @private
   * @function childApi
   * @description API exposed to the child (Permission Layer)
   * @return {Partial<PermissionLayer>}
   */
  private childApi: Partial<PermissionLayer> = {
    connect: (options?: PermissionLayerOptions): void => {
      this.communicationHandler.openPermissionLayer(options);
      this.layerCount = this.layerCount + 1;
      this.layerViewTime = Date.now();
      tracker.track({
        /* eslint-disable @typescript-eslint/camelcase */
        section: 'permission.layer.view',
        cont_layer: 'view',
        coun_loop: this.layerCount,
        time_layer_playout: this.layerViewTime - this.initTime,
        sample: 1
        /* eslint-enable @typescript-eslint/camelcase */
      });
    },

    update: (permissionData: PermissionData | string, status?: _PermissionStatus): void => {
      let permStatus: string;
      if (typeof permissionData === 'string') {
        Logger.warn(`PermissionLayer.update(): The 'permissionType' and 'status' string parameters are deprecated, please call this method with a PermissionData object as the only argument.`);
        this.communicationHandler.setPermissions([{
          usecase: permissionData,
          status,
        }]);
        permStatus = status
      } else {
        this.communicationHandler.setPermissions([permissionData]);
        permStatus = permissionData.status;
      }

      tracker.track({
        /* eslint-disable @typescript-eslint/camelcase */
        section: permStatus.toUpperCase() === 'VALID' ? 'permission.layer.perm.grant' : 'permission.layer.perm.deny',
        cont_perm_usecase: typeof permissionData === 'string' ? permissionData : permissionData.usecase,
        coun_loop: this.layerCount,
        time_action: Date.now() - this.layerViewTime,
        sample: 1
        /* eslint-enable @typescript-eslint/camelcase */
      });
    },

    updateTCData: (tcData: TcData): void => {
      this.communicationHandler.setTCData(tcData);
    },

    next: (nextData?: AditionNextData): void => {
      if (this.layerCount === 0) {
        tracker.track({
          /* eslint-disable @typescript-eslint/camelcase */
          section: ' permission.layer.notview',
          coun_loop: this.layerCount,
          time_process_complete: Date.now() - this.initTime,
          sample: 100
          /* eslint-enable @typescript-eslint/camelcase */
        });
      }
      return this.communicationHandler.updatePermissionLayer(nextData);
    },

    complete: (): void => {
      tracker.track({
        /* eslint-disable @typescript-eslint/camelcase */
        section: 'permission.ation.complete',
        coun_loop: this.layerCount,
        time_process_complete: Date.now() - this.initTime,
        sample: 100
        /* eslint-enable @typescript-eslint/camelcase */
      });
      setTimeout(() => {
        this.communicationHandler.closePermissionLayer();
        this.destroy();
      }, 200);
    },

    getPermissionModel: (): Promise<PermissionModel> => {
      return new Promise((resolve): void => {
        resolve(this.communicationHandler.permissionModel);
      });
    },

    getParameters: (): Promise<LayerParameters> => {
      return this.communicationHandler.getLayerParameters();
    },

    resize: (dimensions: Dimensions): void => {
      this.communicationHandler.resizePermissionLayer(dimensions);
    }
  };

  /**
   * @public
   * @async
   * @function init
   * @description Function to initialize this module
   * @param {PermissionModel} permissionModel The current permission model which will be appended to the ADITION request
   * @param {CommunicationHandlerService} communicationHandler Instance of the `CommunicationHandlerService`
   * @param {AditionNextData} nextData Data to be passed for a single next call
   * @return {Promise<void>}
   */
  public async init(permissionModel: PermissionModel,
                    communicationHandler: CommunicationHandlerService,
                    nextData?: AditionNextData): Promise<void> {
    this.communicationHandler = communicationHandler;

    try {
      if (!this.initTime) {
        this.initTime = Date.now();
      }
      await this.loadAditionFrame(permissionModel, nextData);

      Logger.debug(this.moduleName, `init`, `Module initialized.`);
      return;
    } catch (error) {
      this.childApi.complete();
      Logger.error(this.moduleName, `init`, error);
      return;
    }
  }

  /**
   * @private
   * @async
   * @function loadAditionFrame
   * @description Function to load a adition frame
   * @param {PermissionModel} permissionModel The current permission model
   * @param {AditionNextData} nextData Data to be passed for a single next call
   * @return {Promise<void>}
   */
  private async loadAditionFrame(permissionModel: PermissionModel, nextData?: AditionNextData): Promise<void> {
    try {
      const clientConfig = await this.communicationHandler.getAditionClientConfig();
      const userIdentity = await this.communicationHandler.getIdentity();
      const consentParameters = (await this.communicationHandler.getLayerParameters()).consentParameters;
      const url: string = buildAditionRequest(clientConfig, permissionModel, consentParameters, this.windowId, userIdentity, nextData);
      Logger.debug(this.moduleName, `loadAditionFrame`, `New adition request built: '${url}'.`);

      if (this.iframe) {
        try {
          this.childConnection.destroy();
          // eslint-disable-next-line no-empty
        } catch (ex) {
          // remove the iframe from the DOM if Penpal can't do it
          this.iframe.parentElement.removeChild(this.iframe);
        }
        this.iframe = null;
      }

      this.iframe = document.createElement('iframe');
      const iframeAttributes: AditionIframe = {
        border: '0',
        frameborder: '0',
        marginwidth: '0',
        marginheight: '0',
        allowtransparency: 'true',
        background: 'transparent',
        scrolling: 'yes',
        style: 'display: block; width: 100%; height: 100%; border: 0;'
      };

      Object.keys(iframeAttributes).forEach((attr) => {
        this.iframe.setAttribute(attr, iframeAttributes[attr as keyof AditionIframe]);
      });

      this.childConnection = Penpal.connectToChild({
        url,
        iframe: this.iframe,
        appendTo: document.body,
        timeout: CONFIG_PENPAL_CONNECTION_TIMEOUT,
        methods: this.childApi,
      });

      await this.childConnection.promise;

      Logger.debug(this.moduleName, `loadAditionFrame`, `New adition frame created: `, url);
    } catch (error) {
      /* eslint-disable @typescript-eslint/camelcase */
      tracker.track({
        section: 'permission.layer.view',
        resu_ation: '44000010',
        sample: 1
      });
      /* eslint-enable @typescript-eslint/camelcase */
      this.childApi.complete();
      Logger.error(this.moduleName, `loadAditionFrame`, error);
    }
  }

  /**
   * @public
   * @function destroy
   * @description Function to finish the permission core
   * @return {void}
   */
  public destroy(): void {
    if (this.childConnection) {
      this.childConnection.destroy();
      this.childConnection = null;
    }

    if (this.iframe) {
      this.iframe.src = 'about:blank';
      if (this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe);
      }
      this.iframe = null;
    }

    this.communicationHandler = null;
  }

}
